import { useEffect } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { Button, LoadingSkeleton, Row } from "@narmi/design_system";
import { useFetchTemplates } from "byzantine/src/dbbl/businessLogic/entities/swiftwireTemplates";
import {
  CurrencyProvider,
  useCurrencyContext,
} from "byzantine/src/dbbl/businessLogic/modules/internationalWires";
import { entities, modules } from "byzantine";
import {
  INTERNATIONAL_WIRE_TEMPLATES_PATH,
  TEMPLATES_ROUTE,
} from "../constants";
import InternationalWireTemplateDrawer from "./InternationalWireTemplateDrawer";
import TemplateCard from "./TemplateCard";
import styles from "./InternationalWireTemplatesContainer.module.scss";

const InternationalWireTemplatesContent = () => {
  const { l10n } = useLocalization();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasManageIntlWireTemplatesPermission } =
    modules.wireTemplates.useWirePermissions();
  const sortedTemplateIds =
    entities.swiftwireTemplates.useSortedSwiftWireTemplateIds();
  const {
    send: fetchTemplates,
    loading: loadingTemplates,
    error: errorTemplates,
  } = useFetchTemplates();
  const { send: fetchRecipients, loading: loadingRecipients, error: errorRecipients } =
    entities.recipients.useFetchRecipients();
  const { getCurrencies } = useCurrencyContext();

  const templateRouteMatch = matchPath(
    `/${TEMPLATES_ROUTE}/${INTERNATIONAL_WIRE_TEMPLATES_PATH}/:templateId`,
    location.pathname
  );
  const routeTemplateId = templateRouteMatch?.params.templateId;
  const isDrawerOpen =
    hasManageIntlWireTemplatesPermission && Boolean(routeTemplateId);
  const editTemplateId =
    hasManageIntlWireTemplatesPermission &&
    routeTemplateId &&
    routeTemplateId !== "new"
      ? (routeTemplateId as API.SwiftWireTemplateId)
      : undefined;

  const openDrawer = (templateId?: API.SwiftWireTemplateId) => {
    navigate(
      `/${TEMPLATES_ROUTE}/${INTERNATIONAL_WIRE_TEMPLATES_PATH}/${
        templateId ?? "new"
      }${location.search}`
    );
  };

  const closeDrawer = () => {
    navigate(`/${TEMPLATES_ROUTE}/${INTERNATIONAL_WIRE_TEMPLATES_PATH}${location.search}`);
  };

  useEffect(() => {
    fetchTemplates();
    fetchRecipients();
  }, [fetchTemplates, fetchRecipients]);

  useEffect(() => {
    getCurrencies();
  }, [getCurrencies]);

  if (loadingTemplates || loadingRecipients) {
    return (
      <div className="margin--top--m">
        <LoadingSkeleton isLoading lines={4} />
      </div>
    );
  }

  if (errorTemplates || errorRecipients) {
    return (
      <p className="margin--top--m">
        {l10n.getString("international-wire-templates-error")}
      </p>
    );
  }

  return (
    <>
      <Row justifyContent="end">
        {hasManageIntlWireTemplatesPermission && (
          <Row.Item shrink>
            <Button
              kind="plain"
              startIcon="plus"
              onClick={() => openDrawer()}
              label={l10n.getString("new-template")}
            />
          </Row.Item>
        )}
      </Row>
      {sortedTemplateIds.length === 0 ? (
        <p className="margin--top--m">
          {l10n.getString("international-wire-templates-empty")}
        </p>
      ) : (
        <div className={`margin--top--m ${styles.grid}`}>
          {sortedTemplateIds.map((id) => (
            <TemplateCard
              key={id}
              templateId={id}
              onEdit={() => openDrawer(id)}
              hasManagePermission={hasManageIntlWireTemplatesPermission}
            />
          ))}
        </div>
      )}
      {hasManageIntlWireTemplatesPermission && (
        <InternationalWireTemplateDrawer
          isOpen={isDrawerOpen}
          onDismiss={closeDrawer}
          templateId={editTemplateId}
        />
      )}
    </>
  );
};

const InternationalWireTemplatesContainer = () => (
  <CurrencyProvider>
    <InternationalWireTemplatesContent />
  </CurrencyProvider>
);

export default InternationalWireTemplatesContainer;
